import React from "react";
import { useTranslation } from "react-i18next";
import { PrepFqQuestion } from "../../../../../models/prepFqQuiz";
import clsx from "clsx";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";
import { topicModalStyles } from "../../../styles/topicModalStyles";
import { formClasses, FormRoot } from "../../../../../theme/formStyles";

export const Form = (props: any) => {
  const classes = topicModalStyles();
  const { t } = useTranslation(["prepFq", "common"]);
  const {
    values,
    questions,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setValues,
    setFieldTouched,
  } = props;
  const onChangeTextField = (name: string, e: any) => {
    handleChange(e);
    setFieldTouched(name, true, false);
    if (e.target.value) {
      onSubmit(undefined);
    }
  };
  const onChangeSelection = (name: string, e: any) => {
    const splitName = name.split("_option_");
    if (splitName.length === 2) {
      const question_id = splitName[0];
      const response_index = parseInt(splitName[1]);
      const questionIndex = questions.findIndex(
        (question: PrepFqQuestion) => question.question_id === question_id
      );
      if (questionIndex !== -1) {
        const question: PrepFqQuestion = questions[questionIndex];
        if (response_index === question.response_options.length) {
          const updatedValues = { ...values };
          question.response_options.forEach((option, index) => {
            if (index === question.response_options.length - 1) {
              updatedValues[name] = true;
            } else {
              updatedValues[`${question.question_id}_option_${index + 1}`] =
                false;
            }
          });
          setValues({
            ...updatedValues,
          });
        } else {
          const newValues = {};
          question.response_options.forEach((option, index) => {
            newValues[`${question.question_id}_option_${index + 1}`] =
              values[`${question.question_id}_option_${index + 1}`];
          });
          newValues[name] = e.target.checked;
          if (Object.values(newValues).every((value) => value === false)) {
            setValues({
              ...values,
              ...newValues,
              [`${question.question_id}_option_${question.response_options.length}`]:
                true,
            });
          } else {
            setValues({
              ...values,
              ...newValues,
              [`${question.question_id}_option_${question.response_options.length}`]:
                false,
            });
          }
        }
        setFieldTouched(name, true, false);
        onSubmit(undefined);
      }
    }
  };
  const onSubmit = (e) => {
    e?.preventDefault();
    handleSubmit();
  };
  const generateDropdown = (question: PrepFqQuestion) => {
    return (
      <React.Fragment>
        <FormLabel
          htmlFor={question.question_id}
          className={clsx(formClasses.longFormLabel, classes.whiteText)}
        >
          {question.question_description}
        </FormLabel>
        <TextField
          required
          id={question.question_id}
          name={question.question_id}
          helperText={
            touched[question.question_id]
              ? errors[question.question_id]
                ? `${errors[question.question_id]}`
                : ""
              : ""
          }
          select
          error={
            touched[question.question_id] &&
            Boolean(errors[question.question_id])
          }
          fullWidth
          value={values[question.question_id]}
          onChange={onChangeTextField.bind(null, question.question_id)}
          variant="outlined"
          autoComplete="off"
          classes={{
            root: classes.selectBorder,
          }}
          SelectProps={{
            native: true,
            classes: {
              select: clsx(classes.selectBackground, classes.selectText),
            },
          }}
        >
          <option aria-label="None" value="" />
          {question.response_options.map((option) => (
            <option key={option.response.value} value={option.response.value}>
              {option.response.description}
            </option>
          ))}
        </TextField>
      </React.Fragment>
    );
  };
  const generateSelection = (question: PrepFqQuestion) => {
    return (
      <FormControl
        required
        component="fieldset"
        error={
          touched[question.question_id] &&
          Boolean(
            errors[
              `${question.question_id}_option_${question.response_options.length}`
            ]
          )
        }
      >
        <FormLabel
          htmlFor={question.question_id}
          className={clsx(formClasses.longFormLabel, classes.whiteText)}
        >
          {question.question_description}
        </FormLabel>
        <FormGroup style={{ marginLeft: "24px" }}>
          {question.response_options.map((option, index) => {
            return (
              <FormControlLabel
                style={{ width: "100%" }}
                classes={{
                  label: classes.whiteText,
                }}
                key={`${question.question_id}_option_${index + 1}`}
                control={
                  <Checkbox
                    checked={
                      values[`${question.question_id}_option_${index + 1}`]
                    }
                    onChange={onChangeSelection.bind(
                      null,
                      `${question.question_id}_option_${index + 1}`
                    )}
                    name={`${question.question_id}_option_${index + 1}`}
                    classes={{
                      root: classes.whiteText,
                    }}
                  />
                }
                label={option.response.description}
              />
            );
          })}
        </FormGroup>
        <FormHelperText className={classes.whiteText}>
          {touched[question.question_id]
            ? t(`prepFq:${errors[question.question_id]}`, "")
            : ""}
        </FormHelperText>
      </FormControl>
    );
  };
  const mapFormItemsToComponent = (question: PrepFqQuestion) => {
    switch (question.question_type) {
      case "dropdown":
      case "radio":
        return generateDropdown(question);
      case "selection":
        return generateSelection(question);
      default:
        return generateDropdown(question);
    }
  };
  const generateFormItem = (question: PrepFqQuestion, has_margin: boolean) => {
    return (
      <div
        key={question.question_id}
        style={{ marginLeft: has_margin ? 48 : 0 }}
        className={clsx(formClasses.longFormLabelContainer, formClasses.sparse)}
      >
        {mapFormItemsToComponent(question)}
      </div>
    );
  };
  return (
    <FormRoot
      className={formClasses.root}
      onSubmit={onSubmit}
      style={{ width: "100%" }}
    >
      {(questions as PrepFqQuestion[]).slice().map((question) => {
        if (question.has_prerequisite_question) {
          return (
            <React.Fragment key={question.question_id}>
              {generateFormItem(question.prerequisite_question, false)}
              {(values[question.prerequisite_question.question_id] === 1 ||
                values[question.prerequisite_question.question_id] === "1") &&
                generateFormItem(question, true)}
            </React.Fragment>
          );
        } else if (
          question.has_requirements &&
          question.requirements.type === "question"
        ) {
          return (
            <React.Fragment key={question.question_id}>
              {(values[question.requirements.value] === 1 ||
                values[question.requirements.value] === "1") &&
                generateFormItem(question, true)}
            </React.Fragment>
          );
        } else {
          return generateFormItem(question, false);
        }
      })}
    </FormRoot>
  );
};
